import React from 'react';
import {
  Alert,
  AlertTitle,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

const providerName = (provider) => {
  if (provider === 'azure') return 'Azure OpenAI';
  if (provider === 'gemini') return 'Gemini';
  return 'OpenAI';
};

const credentialStatus = (workload = {}) => (
  workload.parentKeyConfigured ? 'Key configured' : 'Key required'
);

const UniversityAiBillingStatus = ({
  configuration,
  historyCostTokens = 0,
  paymentRequired = false,
  walletBalance = 0,
}) => {
  const fundingMode = configuration?.funding?.mode;

  if (!fundingMode) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>AI billing ownership</Typography>
          <Divider sx={{ mb: 2 }} />
          <Alert severity="warning">
            AI billing ownership could not be confirmed. Refresh before making billing decisions.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (fundingMode === 'tenant_byok') {
    const workloads = [
      {
        label: 'Realtime consultation',
        provider: configuration.realtime?.provider,
        status: credentialStatus(configuration.realtime),
      },
      {
        label: 'Case marking',
        provider: configuration.marking?.provider,
        status: credentialStatus(configuration.marking),
      },
      configuration.transcription?.enabled
        ? {
          label: 'Input transcription',
          provider: configuration.transcription.provider,
          status: credentialStatus(configuration.transcription),
        }
        : {
          label: 'Input transcription',
          provider: null,
          status: 'Disabled',
        },
    ];

    return (
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h6">AI billing ownership</Typography>
            <Chip size="small" color="success" label="University managed" />
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Alert severity="success" sx={{ mb: 2 }}>
            Your university supplies its own provider credentials and is billed directly by those providers. Virtual Medic does not bill these AI requests.
          </Alert>
          <Stack spacing={1.25}>
            {workloads.map((workload) => (
              <Stack
                key={workload.label}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                spacing={0.5}
              >
                <Typography variant="body2">{workload.label}</Typography>
                <Typography
                  variant="body2"
                  color={workload.status === 'Key required' ? 'error.main' : 'text.secondary'}
                  sx={{ fontWeight: 700 }}
                >
                  {workload.provider ? `${providerName(workload.provider)} · ` : ''}{workload.status}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const numericBalance = Number(walletBalance) || 0;
  const numericCost = Number(historyCostTokens) || 0;
  const walletDepleted = paymentRequired || (numericCost > 0 && numericBalance < numericCost);

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h6">Platform AI wallet</Typography>
          <Chip size="small" color="primary" label="Platform managed" />
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Stack spacing={1}>
          <Typography variant="body2">
            Balance: <strong>{numericBalance} tokens</strong>
          </Typography>
          <Typography variant="body2">
            Tokens required per completed history: {numericCost}
          </Typography>
          {numericCost > 0 && (
            <Typography variant="body2">
              Approximately {Math.floor(numericBalance / numericCost)} histories remaining
            </Typography>
          )}
          {walletDepleted && (
            <Alert severity="warning">
              <AlertTitle>AI wallet depleted</AlertTitle>
              Ask your Virtual Medic account owner to add credits before further case launches.
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default UniversityAiBillingStatus;
